/** @jsx jsx */
// eslint-disable-next-line
import React from 'react'
import { Button, CircularProgress, MenuItem, TextField, withStyles } from '@material-ui/core'
import { css, jsx } from '@emotion/core'
import PropTypes from 'prop-types'
import { toast } from 'react-toastify'
import { theme } from '../../../constants'
import OrganizationService from '../../../services/organization'
import formatDate from '../../../utils/formatDate'
import Multiselect from '../../components/Multiselect'
import * as Actions from '../../admin/actions'
import { getPlanName } from '../../../utils/planName'

const styles = {}

styles.blockingOverlay = css`
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  z-index: 999;
  background: rgba(0, 0, 0, 0.5);
`

styles.box = css`
  position: absolute;
  width: 750px;
  border-radius: 5px;
  padding: 30px 60px;
  background: white;
  border: 10px solid lightgray;
  pointer-events: all;
`

styles.title = css`
  display: flex;
  width: 100%;
  justify-content: center;
  margin-bottom: 20px;
  font-size: 20px;
  font-weight: bold;
  color: ${theme.palette.common.black};
`

styles.error = css`
  color: ${theme.palette.error.main};
  margin: 0px 30px 15px 30px;
`

styles.emailsContainer = css`
  display: flex;
  width: 100%;
  flex-direction: column;
  margin: 18px 0px;
  color: ${theme.palette.common.black};
`

styles.descriptionContainer = css`
  display: flex;
  width: 100%;
  height: 40px;
  justify-content: space-between;
  margin: 14px 0px;
  color: ${theme.palette.common.black};
`

styles.label = css`
  width: 30%;
  display: flex;
  align-items: center;
  font-size: 14px;
  font-weight: bold;
`

styles.description = css`
  width: 70%;
  display: flex;
  align-items: center;
  font-size: 14px;
`

styles.selectForm = css`
  width: 100%;
  height: 40px;
  font-weight: 500;
  background: transparent;

  &:focus {
    outline: 2px solid #4a90e2; /* example color */
    outline-offset: 2px; /* optional: separates the outline from the element */
  }
`

styles.actionContainer = css`
  display: flex;
  align-items: center;
  justify-content: end;
  margin-top: 10px;
`

styles.actionButton = css`
  height: 24px;
  padding: 0px 20px;
  text-transform: none;
  font-size: 14px;
  background: ${theme.palette.common.blue};
  color: ${theme.palette.common.white};

  &:hover {
    background: ${theme.palette.common.blue2};
  }
`

styles.cancelButton = css`
  height: 24px;
  padding: 0px 20px;
  margin-right: 15px;
  text-transform: none;
  font-size: 14px;
  background: transparent;
  color: ${theme.palette.common.blue};
  box-shadow: none;

  &:hover {
    background: transparent;
  }
`

styles.loaderContainer = css`
  display: flex;
  color: white;
  margin-right: 10px;
`

styles.error = css`
  color: ${theme.palette.error.main};
  height: 20px;
  text-align: right;
  font-size: 14px;
`

styles.iconClose = css`
  position: absolute;
  display: flex;
  color: grey;
  top: 20px;
  right: 24px;
  align-items: flex-start;
  justify-content: flex-end;
  font-size: 28px;
  cursor: pointer;
`

const CssSelect = withStyles({
  root: {
    '& .MuiOutlinedInput-input': {
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'start',
      backgroundColor: 'transparent',
      paddingBottom: '8px',
      paddingTop: '10px',
      fontSize: '14px'
    },

    '& .MuiOutlinedInput-root': {
      '&.Mui-focused fieldset': {
        borderColor: theme.palette.text.disabled
      }
    }
  }
})(TextField)

const CssMenuItem = withStyles({
  root: {
    fontSize: '14px'
  }
})(MenuItem)

const CssTextField = withStyles({
  root: {
    '& label.Mui-focused': {
      color: theme.palette.common.black
    },
    '& .MuiInput-underline:after': {
      borderBottomColor: `${theme.palette.text.disabled}`
    },
    '& .Mui-disabled': {
      color: `${theme.palette.text.disabled} !important`
    },
    '& .MuiOutlinedInput-multiline': {
      padding: '12px',
      color: theme.palette.common.black
    },
    '& .MuiOutlinedInput-root': {
      '& fieldset': {
        borderColor: `${theme.palette.text.disabled} !important`,
        borderRadius: '8px'
      }
    }
  }
})(TextField)

const inputText = {
  input: { color: theme.palette.common.black },
  label: { color: theme.palette.common.black },
  width: '100%',
  marginTop: '8px',
  marginBottom: 0
}

const CssMultiselect = withStyles({
  root: {
    width: '100%',

    '& .MuiFormLabel-root': {
      color: theme.palette.common.black,
      fontSize: '19px',
      fontWeight: 'bold'
    },
    '& .MuiInput-formControl': {
      marginTop: '24px'
    },
    '& .MuiInput-underline:after': {
      borderBottomColor: `${theme.palette.text.disabled}`
    },
    '& .MuiOutlinedInput-root': {
      '&.Mui-focused fieldset': {
        borderColor: theme.palette.text.disabled
      }
    },
    '& .MuiChip-root': {
      color: theme.palette.common.white,
      background: theme.palette.common.blue
    },
    '& .MuiChip-deleteIcon': {
      color: 'rgba(255, 255, 255, 0.75)',

      '&:hover': {
        color: theme.palette.common.white
      }
    }
  }
})(Multiselect)

const CssButton = withStyles({
  root: {
    '&.Mui-disabled': {
      color: 'rgba(255, 255, 255, 0.5)'
    }
  }
})(Button)

function AddOrganizationUsersModal(props) {
  // Count pending users with the user-key assigned to them.
  function getAvailableCount(userKey) {
    let availableCount = userKey.max_usage - userKey.count_usage
    for (const organizationUser of props.ownerOrganization.users) {
      if (
        !organizationUser.user &&
        organizationUser.user_key &&
        organizationUser.user_key.code === userKey.code &&
        availableCount > 0
      ) {
        availableCount -= 1
      }
    }

    return availableCount
  }

  const firstAvailableUserKey = props.subscriptions.reduce((acc, v) => {
    if (acc) {
      return acc
    }

    if (v.order) {
      for (let i = 0; i < v.order.items.length; i += 1) {
        const iUserKey = v.order.items[i].user_key
        const iUserKeyBaseTemplate =
          iUserKey && iUserKey.licence_configuration && iUserKey.licence_configuration.base_template
        if (iUserKeyBaseTemplate && getAvailableCount(iUserKey) > 0) {
          return iUserKey
        }
      }

      // The plan selector only lists the user-keys of the order items.
      return null
    }

    const vUserKeyBaseTemplate =
      v.userKey && v.userKey.licence_configuration && v.userKey.licence_configuration.base_template
    if (vUserKeyBaseTemplate && getAvailableCount(v.userKey) > 0) {
      return v.userKey
    }

    return null
  }, null)

  const [emails, setEmails] = React.useState('')
  const [userKey, setUserKey] = React.useState(firstAvailableUserKey)
  const [role, setRole] = React.useState('member')
  const [organizations, setOrganizations] = React.useState([])
  const [allOrganizations, setAllOrganizations] = React.useState([])
  const [suggestions, setSuggestions] = React.useState([])
  const [isEnabled, setIsEnabled] = React.useState(false)
  const [isLoading, setIsLoading] = React.useState(false)
  const [showError, setShowError] = React.useState('')

  React.useEffect(() => {
    const allOrgs = props.groupOrganizations.map(v => ({ value: v._id, label: v.name }))
    setAllOrganizations(allOrgs)
    setSuggestions(allOrgs)
  }, [props.groupOrganizations])

  function validateEmails(emailList, selectedUserKey) {
    const emailElements = emailList
      .split(',')
      .map(v => v.trim())
      .filter(v => v.length > 0)
      .filter((v, i, list) => list.findIndex(w => w.toLowerCase() === v.toLowerCase()) === i)
    const isValid =
      emailElements.length > 0 &&
      emailElements.every(v => {
        const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/
        return emailRegex.test(v)
      })

    if (!isValid) {
      setShowError('Please enter valid email addresses separated by commas')
      setIsEnabled(false)
    } else if (
      emailElements.some(e =>
        props.organizationUsers.find(u => u.email.toLowerCase() === e.toLowerCase())
      )
    ) {
      setShowError('Some of the users are already in the organization')
      setIsEnabled(false)
    } else if (selectedUserKey) {
      // Count pending users with the user-key assigned to them.
      let availableCount = selectedUserKey.max_usage - selectedUserKey.count_usage
      for (const organizationUser of props.ownerOrganization.users) {
        if (
          !organizationUser.user &&
          organizationUser.user_key &&
          organizationUser.user_key.code === selectedUserKey.code &&
          availableCount > 0
        ) {
          availableCount -= 1
        }
      }

      if (emailElements.length > availableCount) {
        setShowError(`You can only add up to ${availableCount} users with this plan`)
        setIsEnabled(false)
      } else {
        setShowError('')
        setIsEnabled(true)
      }
    } else {
      setShowError('')
      setIsEnabled(true)
    }
  }

  function onEmailsChange(event) {
    const emailList = event.target.value
    validateEmails(emailList, userKey)
    setEmails(emailList)
  }

  function onPlanChange(event) {
    const userKeyCode = event.target.value
    const subscriptionUserKey = props.subscriptions.reduce((acc, v) => {
      if (acc) {
        return acc
      }

      if (v.order) {
        for (let i = 0; i < v.order.items.length; i += 1) {
          const iUserKey = v.order.items[i].user_key
          if (iUserKey && iUserKey.code === userKeyCode) {
            return iUserKey
          }
        }
      }

      if (v.userKey && v.userKey.code === userKeyCode) {
        return v.userKey
      }

      return null
    }, null)
    setUserKey(subscriptionUserKey)

    // Re-check the emails against the seats of the new plan.
    if (emails) {
      validateEmails(emails, subscriptionUserKey)
    }
  }

  function onRoleChange(event) {
    const roleId = event.target.value
    setRole(roleId)
  }

  function onChangeOrganizations(userOrganizations) {
    setOrganizations(userOrganizations)

    const suggestionOrganizations = allOrganizations.filter(
      v => !userOrganizations.some(w => v.value === w.value)
    )
    setSuggestions(suggestionOrganizations)
  }

  async function onSubmit() {
    // On submit, show loader and disable submit to not perform many retries with the same key.
    setIsLoading(true)
    setIsEnabled(false)

    try {
      const payload = {
        emails: emails
          .split(',')
          .map(v => v.trim())
          .filter(v => v.length > 0)
          .filter((v, i, list) => list.findIndex(w => w.toLowerCase() === v.toLowerCase()) === i),
        role,
        userKey: userKey ? userKey._id : null,
        organizations: organizations.map(v => v.value)
      }
      await OrganizationService.addUsers(props.ownerOrganization._id, payload)

      // Reload UI.
      await Actions.updateUser()
      onClose()
    } catch (error) {
      const message = error.response && error.response.data && error.response.data.message
      if (message) {
        setShowError(message)
        if (!showError) {
          setTimeout(() => setShowError(''), 5000)
        }

        setIsLoading(false)
      } else {
        console.error(error)
        toast.error(window.I18n.t('users.addErrorMessage'))
        onClose()
      }
    }
  }

  function onClose() {
    props.onApply()
  }

  return (
    <div css={styles.blockingOverlay}>
      <div css={styles.box}>
        <span css={styles.title}>{window.I18n.t('users.addUsers')}</span>
        <div css={styles.emailsContainer}>
          <span css={styles.label} style={{ width: '100%' }}>
            {window.I18n.t('users.addEmails')}
          </span>
          <CssTextField
            variant="outlined"
            placeholder={window.I18n.t('users.useComma')}
            value={emails}
            onChange={onEmailsChange}
            type="email"
            name="email"
            margin="normal"
            css={inputText}
            multiline
            rows={2}
          />
        </div>
        <div css={styles.descriptionContainer}>
          <span css={styles.label}>{window.I18n.t('users.plan')}</span>
          <span css={styles.description}>
            <CssSelect
              select
              variant="outlined"
              labelId="plan"
              id="plan"
              onChange={onPlanChange}
              value={userKey ? userKey.code : 'unassigned'}
              css={styles.selectForm}
            >
              {props.subscriptions
                .reduce((acc, v) => {
                  if (v.order) {
                    for (let i = 0; i < v.order.items.length; i += 1) {
                      const iUserKey = v.order.items[i].user_key
                      const iUserKeyBaseTemplate =
                        iUserKey &&
                        iUserKey.licence_configuration &&
                        iUserKey.licence_configuration.base_template
                      if (iUserKeyBaseTemplate && iUserKey.max_usage > iUserKey.count_usage) {
                        acc.push({ ...v, userKey: iUserKey, planName: getPlanName(iUserKey) })
                      }
                    }
                  } else if (v.userKey) {
                    acc.push(v)
                  }

                  return acc
                }, [])
                .map(v => {
                  // Count pending users with the user-key assigned to them.
                  let availableCount = v.userKey.max_usage - v.userKey.count_usage
                  for (const organizationUser of props.ownerOrganization.users) {
                    if (
                      !organizationUser.user &&
                      organizationUser.user_key &&
                      organizationUser.user_key.code === v.userKey.code &&
                      availableCount > 0
                    ) {
                      availableCount -= 1
                    }
                  }

                  const renewalAt =
                    v.order && (v.order.renewal_at || v.userKey.licence_expiration_date)
                  const renewal = renewalAt
                    ? window.I18n.t('users.renewsOn', { date: formatDate(renewalAt) })
                    : ''

                  return (
                    <CssMenuItem value={v.userKey.code}>
                      {window.I18n.t('users.availableLicenses', {
                        planName: v.planName,
                        count: availableCount,
                        renewal
                      })}
                    </CssMenuItem>
                  )
                })}
              <CssMenuItem value={'unassigned'}>{window.I18n.t('common.unassigned')}</CssMenuItem>
            </CssSelect>
          </span>
        </div>

        <div css={styles.descriptionContainer}>
          <span css={styles.label}>{window.I18n.t('users.role')}</span>
          <span css={styles.description}>
            <CssSelect
              select
              variant="outlined"
              labelId="role"
              id="role"
              onChange={onRoleChange}
              value={role}
              css={styles.selectForm}
            >
              <CssMenuItem value="admin">{window.I18n.t('users.roles.admin')}</CssMenuItem>
              <CssMenuItem value="member">{window.I18n.t('users.roles.member')}</CssMenuItem>
            </CssSelect>
          </span>
        </div>

        <div>
          <CssMultiselect
            name="organizations"
            label={window.I18n.t('users.userGroups')}
            placeholder={window.I18n.t('users.enterGroup')}
            onChange={onChangeOrganizations}
            fetchSuggestions={false}
            suggestions={suggestions}
            initialSelected={organizations}
          />
        </div>

        <div css={styles.error}>{showError}</div>
        <div css={styles.actionContainer}>
          <CssButton
            color="primary"
            variant="contained"
            css={styles.cancelButton}
            disabled={isLoading}
            onClick={onClose}
          >
            {window.I18n.t('common.cancel')}
          </CssButton>
          <CssButton css={styles.actionButton} disabled={!isEnabled} onClick={onSubmit}>
            {isLoading && (
              <div css={styles.loaderContainer}>
                <CircularProgress size={16} thickness={4} color="inherit" />
              </div>
            )}
            {window.I18n.t('common.add')}
          </CssButton>
        </div>
        <span
          css={styles.iconClose}
          style={isLoading ? { opacity: 0.5, pointerEvents: 'none' } : {}}
          onClick={onClose}
        >
          {' '}
          x{' '}
        </span>
      </div>
    </div>
  )
}

AddOrganizationUsersModal.defaultProps = {}

AddOrganizationUsersModal.propTypes = {
  user: PropTypes.object.isRequired,
  organizationUsers: PropTypes.array.isRequired,
  groupOrganizations: PropTypes.array.isRequired,
  ownerOrganization: PropTypes.object.isRequired,
  subscriptions: PropTypes.array.isRequired,
  onApply: PropTypes.func.isRequired
}

export default AddOrganizationUsersModal
